import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { registerPinPrompter } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { AlertCircle, KeyRound, Loader2, ShieldCheck } from "lucide-react";

/**
 * Provider global do PIN de colaborador exigido em toda ALTERAÇÃO no
 * sistema (POST/PUT/PATCH/DELETE). O interceptor do axios (registrado
 * abaixo) detecta status 428 (PIN faltando) ou 401 (PIN errado) e chama
 * `promptPin(label)` para abrir este modal — o usuário digita
 * código+senha e a requisição é reenviada automaticamente com os
 * headers `X-Collab-Code` e `X-Collab-Password`.
 *
 * Motivo do fluxo: uma única estação pode ser usada por vários
 * colaboradores durante o dia. O PIN garante que cada alteração fique
 * gravada em `movement_logs` com o nome/código do colaborador que
 * realmente executou a ação — para consulta em Auditoria/Apontamentos.
 */

const CollabPinContext = createContext(null);

export function useCollabPin() {
  const ctx = useContext(CollabPinContext);
  if (!ctx) throw new Error("useCollabPin: envolva a árvore em <CollabPinProvider>");
  return ctx;
}

export default function CollabPinProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const resolverRef = useRef(null);

  const promptPin = useCallback((actionLabel = "esta alteração") => {
    return new Promise((resolve) => {
      // Fecha toasts em andamento (ex: "Executando backup...") para não
      // ficarem visualmente empilhados sobre o modal do PIN — a operação
      // real fica pausada aguardando o PIN.
      try { toast.dismiss(); } catch (err) { console.debug("toast.dismiss falhou:", err?.message); }
      resolverRef.current = resolve;
      setLabel(actionLabel);
      setError("");
      setCode("");
      setPassword("");
      setOpen(true);
    });
  }, []);

  // Registra o prompter no cliente axios ao montar
  useEffect(() => {
    registerPinPrompter(promptPin);
    return () => registerPinPrompter(null);
  }, [promptPin]);

  const finalize = (value) => {
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
    setOpen(false);
    setSubmitting(false);
  };

  const cancel = () => finalize(null);

  const submit = (e) => {
    e?.preventDefault?.();
    const c = code.trim();
    const p = password;
    if (!c || !p) {
      setError("Informe código e senha");
      return;
    }
    setSubmitting(true);
    finalize({ code: c, password: p });
  };

  return (
    <CollabPinContext.Provider value={{ promptPin }}>
      {children}
      <Dialog open={open} onOpenChange={(v) => { if (!v) cancel(); }}>
        <DialogContent
          className="sm:max-w-md bg-white text-black border border-black shadow-[6px_6px_0_rgba(0,0,0,0.15)]"
          style={{ zIndex: 2147483000 }}
          data-testid="collab-pin-dialog"
          onEscapeKeyDown={(e) => { e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-black">
              <ShieldCheck className="w-5 h-5 text-[color:var(--pop-orange,#f97316)]" />
              Autenticação do colaborador
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              Para registrar quem está realizando <b>{label}</b>, digite seu
              código e senha. Essa ação ficará gravada em Auditoria.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="collab-pin-code" className="text-black">Código do colaborador</Label>
              <Input
                id="collab-pin-code"
                data-testid="collab-pin-code-input"
                inputMode="numeric"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Ex: 001"
                autoComplete="off"
                className="bg-white text-black border-black placeholder:text-zinc-400"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="collab-pin-password" className="text-black">Senha</Label>
              <Input
                id="collab-pin-password"
                data-testid="collab-pin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sua senha"
                autoComplete="current-password"
                className="bg-white text-black border-black placeholder:text-zinc-400"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600" data-testid="collab-pin-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={cancel} data-testid="collab-pin-cancel"
                className="bg-white text-black border-black hover:bg-zinc-100">
                Cancelar
              </Button>
              <Button type="submit" data-testid="collab-pin-submit" disabled={submitting}
                className="bg-black text-white hover:bg-zinc-800">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Confirmar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </CollabPinContext.Provider>
  );
}
